<template>
  <div class="relative z-10">
    <!-- Header Section -->
    <div class="text-center mb-8 lg:mb-12">
      <h2 class="text-3xl lg:text-[48px] mb-4">
        <WrGradientText append-class="pb-2" text="Selected Projects" />
      </h2>
      <p class="md:text-lg tracking-[0.5px] text-[var(--secondary-foreground)]">
        Vision Realized, Software Perfected
      </p>
    </div>

    <!-- Tabs Component -->
    <Tabs value="0" :pt="{
      root: { class: 'w-full' },
      tablist: {
        class:
          'flex justify-center gap-0 mb-8 !bg-transparent border-0 p-0 overflow-x-auto',
      },
      tab: {
        class: '!bg-transparent border-0 p-0',
      },
      tabpanel: {
        class: 'p-0 bg-transparent',
      },
    }">
      <!-- Tab Headers -->
      <TabList class="w-full mb-4 md:mb-8 bg-transparent border-0 rounded-xl overflow-hidden" :pt="{
        tablist: {
          class: 'max-lg:overflow-x-auto no-scrollbar !bg-transparent gap-3',
        },
        content: { class: 'w-full' },
        activeBar: { class: 'hidden' },
      }">
        <Tab v-for="(project, index) in projects" :key="index" :value="index.toString()"
          class="rounded-xl dark:rounded-b-none bg-white dark:bg-transparent !px-4 !py-3 !text-base !font-medium !border-b-4 !border-[var(--primary)] !transition-all !duration-300 !cursor-pointer !whitespace-nowrap"
          :class="{
            '!text-[var(--primary-color)] !border-[var(--primary)]':
              activeTab === index.toString(),
            '!text-[var(--muted-foreground)] !border-transparent hover:!text-[var(--foreground)] hover:!border-[var(--border-color)]':
              activeTab !== index.toString(),
          }" @click="activeTab = index.toString()">
          {{ project.title }}
        </Tab>
      </TabList>

      <!-- Tab Panels -->
      <TabPanel v-for="(project, index) in projects" :key="index" :value="index.toString()">
        <div class="max-w-6xl mx-auto">
          <!-- Main Content Layout -->
          <div class="grid lg:grid-cols-12 gap-4 md:gap-8">
            <!-- Left Sidebar - Highlights -->
            <div class="max-md:order-2 lg:col-span-3 h-full">
              <div
                class="bg-[var(--card)] rounded-2xl py-6 dark:border border-[var(--primary-invert)] bg-white dark:bg-transparent h-full">
                <h3 class="text-lg font-semibold text-[var(--foreground)] mb-4 pl-6">
                  Highlights
                </h3>
                <div class="md:space-y-4">
                  <div v-for="highlight in project.highlights" :key="highlight"
                    class="flex items-start gap-3 px-6 py-3 rounded-lg hover:bg-[var(--accent)] transition-colors duration-200">
                    <div class="w-1.5 h-1.5 rounded-full bg-[var(--primary)] mt-2 shrink-0"></div>
                    <span class="text-sm text-[var(--muted-foreground)] leading-relaxed">{{ highlight }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Area -->
            <div class="max-md:order-1 lg:col-span-9">
              <div
                class="bg-[var(--card)] rounded-2xl dark:border border-[var(--primary-invert)] overflow-hidden h-full bg-white dark:bg-transparent">
                <!-- Project Screenshot/Preview -->
                <div>
                  <div class="text-center">
                    <!-- <h4 class="text-xl font-semibold text-[var(--foreground)] mb-2">{{ project.title }}</h4>
                    <p class="text-[var(--muted-foreground)]">Interactive Dashboard Preview</p> -->
                    <NuxtImg :src="project.img" class="w-full" loading="lazy"
                      :alt="project.title + ' dashboard user interface'" format="webp" />
                  </div>
                </div>

                <!-- Project Details -->
                <div class="p-6">
                  <div class="grid md:grid-cols-2 gap-6">
                    <!-- Tech Stack -->
                    <div>
                      <h4 class="text-sm font-semibold text-[var(--foreground)] mb-3 uppercase tracking-wide">
                        Technology Stack
                      </h4>
                      <div class="flex flex-wrap gap-2">
                        <WrTag v-for="tech in project.tech.split(', ')" :key="tech">
                          {{ tech }}
                        </WrTag>
                      </div>
                    </div>

                    <!-- Project Stats -->
                    <div>
                      <h5 class="text-sm font-semibold text-[var(--foreground)] mb-3 uppercase tracking-wide">
                        Project Info
                      </h5>
                      <div class="space-y-2">
                        <div class="flex justify-between items-center">
                          <span class="text-sm text-[var(--muted-foreground)]">Status</span>
                          <span class="text-sm font-medium text-green-700">Completed</span>
                        </div>
                        <div class="flex justify-between items-center">
                          <span class="text-sm text-[var(--muted-foreground)]">Duration</span>
                          <span class="text-sm font-medium text-[var(--foreground)]">6 months</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TabPanel>
    </Tabs>
  </div>
</template>

<script setup>
import { ref } from "vue";

const activeTab = ref("0");

const projects = ref([
  {
    id: "01",
    title: "Real Time",
    tech: "PHP, Laravel, Vue, Tailwind",
    img: './images/real.webp',
    highlights: [
      "Live Field Agent Tracking",
      "Real Time Shipment Updates",
      "Push Notifications",
      "Chat & Bots",
      "Real Time Messaging",
      "Multi User Collaboration",
      "Custom Video Meeting Interface",
    ],
  },
  {
    id: "02",
    title: "Order Management",
    tech: "PHP, Laravel, Vue, Tailwind",
    img: './images/order-management.webp',
    highlights: [
      "Inventory Management",
      "Real Time Stock",
      "Low Latency API",
      "Payment Attempts & Failure Reports",
      "Omnichannel Fulfillment",
      "Warehouse & Vendor Management",
    ],
  },
  {
    id: "03",
    title: "Analytic",
    tech: "PHP, Laravel, Vue, Tailwind",
    img: './images/analytics.webp',
    highlights: [
      "Live Visitors",
      "Custom Granular Filters",
      "Easy Integration",
      "Customizable Charts",
      "Data Analysis & Recommendations",
      "High Performance Data Queries",
    ],
  },
  {
    id: "04",
    title: "Learning Management System",
    tech: "PHP, Laravel, Vue, Tailwind",
    img: './images/learning-management-system-ui.webp',
    highlights: [
      "Real Time Collaboration",
      "Customizable Whiteboard",
      "35+ Types of Questions for Quiz, Test & Exams",
      "Various Type of Courses & Lesson",
      "Protected Videos & Assets",
      "Customizable Roles & Permissions",
    ],
  },
  {
    id: "05",
    title: "Live Streaming",
    tech: "PHP, Laravel, Vue, Tailwind",
    img: './images/live-streaming.webp',
    highlights: [
      "Mux Integration",
      "Fast Frontend & Mobile App",
      "Stripe Connect Payment",
      "Live Stream Recording",
      "< 10 Seconds Global Latency",
      "Streamer & Viewer Chat,Comment & Reactions",
    ],
  },
]);
</script>
